import { Fragment } from "react";
import {
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showError } from "../actions/checkoutStore";
import { allowedCountries } from "../utils/StripeExpressCheckoutPaymentRequestUtils";

export default function StripeExpressCheckoutPaymentRequest(props) {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const { summary, shipping, information, urls, colors } = props;

  const handleShippingOption = async (event) => {
    await axios({
      headers: {
        common: {
          "Content-Type": "application/json",
        },
      },
      method: "post",
      url: urls.change_shipping_option,
      data: {
        shipping_method_id: event.shippingRate.id,
      },
      params: { recover_token: window.token },
    }).then(function (result) {
      elements.update({
        amount: parseInt(result.data.amount * summary.subunit_to_unit),
      });
      event.resolve({
        status: "success",
      });
    });
  };

  const handleShippingAddress = async (event) => {
    if (!allowedCountries.includes(event.address.country)) {
      event.reject({ status: "invalid_shipping_address" });
    } else {
      // Perform server-side request to fetch shipping options
      const shippingAddress = {
        shipping: {
          name: event.address.recipient,
          phone: event.address.phone,
          address: {
            city: event.address.city,
            postal_code: event.address.postal_code,
            state: event.address.state || event.address.region,
            country: event.address.country,
          },
        },
      };

      await axios({
        headers: {
          common: {
            "Content-Type": "application/json",
          },
        },
        method: "post",
        url: urls.estimate_shipping,
        data: shippingAddress,
        params: { recover_token: window.token },
      }).then(function (result) {
        if (result.data.length > 0) {
          const shippingOptions = result.data.map((estimate) => {
            const shipping_method_id = estimate.table.shipping_method_id;
            const shipping_service_id = estimate.table.shipping_service_id
              ? estimate.table.shipping_service_id
              : "";
            let amount = parseInt(
              estimate.table.price * summary.subunit_to_unit,
            );

            return {
              id: `${shipping_method_id}-${shipping_service_id}`,
              displayName: estimate.table.shipping_method_name,
              amount: amount,
            };
          });
          event.resolve({
            shippingRates: shippingOptions,
          });
        } else {
          event.reject({
            status: "fail",
          });
        }
      });
    }
  };

  const buildShippingRates = (shipping, information) => {
    let shippingOptions = [];

    if (information.shipping_option == "no_shipping") {
      return shippingOptions;
    }

    const chosenShippingMethodID = `${shipping.shipping_ids.shipping_method_id}-${shipping.shipping_ids.shipping_service_id ? shipping.shipping_ids.shipping_service_id : ""}`;
    const chosenShippingMethod = shipping.shipping_methods.find(
      (shippingMethod) => {
        const shippingMethodID = `${shippingMethod.table.shipping_method_id}-${shippingMethod.table.shipping_service_id ? shippingMethod.table.shipping_service_id : ""}`;
        return shippingMethodID === chosenShippingMethodID;
      },
    );

    if (chosenShippingMethod !== undefined) {
      const shipping_method_id = chosenShippingMethod.table.shipping_method_id;
      const shipping_service_id = chosenShippingMethod.table.shipping_service_id
        ? chosenShippingMethod.table.shipping_service_id
        : "";
      let amount = parseInt(
        chosenShippingMethod.table.price_unformatted * summary.subunit_to_unit,
      );

      shippingOptions.push({
        id: `${shipping_method_id}-${shipping_service_id}`,
        displayName: chosenShippingMethod.table.shipping_method_name,
        amount: amount,
      });
    }
    return shippingOptions;
  };

  const isShippingRequired = (information) => {
    if (
      information.shipping_option == "delivery" ||
      information.shipping_option == "store_pickup"
    ) {
      return true;
    }

    return false;
  };

  const onConfirm = async (event) => {
    const paymentDetails = {
      payment_method: event.paymentDetails,
      billing_details: {
        name: event.billingDetails.name,
        email: event.billingDetails.email,
        address: event.billingDetails.address,
        phone: event.billingDetails.phone,
      },
      shipping_details: event.shippingAddress,
      shipping_option: event.shippingRate,
      recover_token: window.token,
    };

    const response = await axios({
      method: "POST",
      url: urls.create_payment_intent,
      data: {
        paymentDetails,
      },
      params: { recover_token: window.token },
    })
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        event.paymentFailed();
        dispatch(showError(error));
      });

    const clientSecret = response.client_secret;
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: "",
      },
      redirect: "if_required",
    });

    if (error) {
      event.paymentFailed();
      dispatch(showError(error));
    } else {
      await axios({
        headers: {
          common: {
            "Content-Type": "application/json",
          },
        },
        method: "put",
        url: urls.fast_place_order,
        data: paymentIntent,
        params: { recover_token: window.token },
      })
        .then((_response) => {
          window.location = urls.success;
        })
        .catch((error) => {
          event.paymentFailed();
          dispatch(showError(error));
        });
    }
  };

  const getButtonColor = (colors) => {
    return colors.is_dark_mode ? "black" : "white";
  };

  const options = {
    emailRequired: true,
    phoneNumberRequired: true,
    allowedShippingCountries: allowedCountries,
    shippingAddressRequired: isShippingRequired(information),
    shippingRates: buildShippingRates(shipping, information),
    buttonTheme: {
      googlePay: getButtonColor(colors),
      applePay: getButtonColor(colors),
    },
    paymentMethods: {
      applePay: "always",
      googlePay: "always",
    },
  };

  return (
    <Fragment>
      <div className="col-12">
        <div className="row">
          <div className="d-flex mx-auto mb-4" style={{ width: "300px" }}>
            <ExpressCheckoutElement
              options={options}
              onConfirm={onConfirm}
              onShippingAddressChange={handleShippingAddress}
              onShippingRateChange={handleShippingOption}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
}
